/**
 * Manual Section Component
 *
 * Renders individual manual section with markdown content and breadcrumb navigation.
 * Story 7-2, Story 1-4 (Epic 10)
 */

import { useState, useEffect } from 'react';
import { marked } from 'marked';
import { ManualSection as ManualSectionType, SectionWithPath } from '../../lib/manualApi';
import { ManualBreadcrumb } from './ManualBreadcrumb';

interface ManualSectionProps {
  section: ManualSectionType | SectionWithPath;
  /** Optional callback for breadcrumb navigation */
  onNavigate?: (slug: string) => void;
}

// Configure marked options for security and styling
marked.setOptions({
  gfm: true,
  breaks: true,
});

/**
 * Type guard to check if section has path property
 */
function hasBreadcrumbPath(section: ManualSectionType | SectionWithPath): section is SectionWithPath {
  return 'path' in section && Array.isArray(section.path);
}

export function ManualSection({ section, onNavigate }: ManualSectionProps) {
  const [htmlContent, setHtmlContent] = useState<string>('');

  // Parse markdown asynchronously (marked v4+ returns Promise)
  useEffect(() => {
    const parseMarkdown = async () => {
      try {
        const html = await marked.parse(section.content);
        setHtmlContent(html);
      } catch {
        // Markdown parsing failed - show user-friendly error
        setHtmlContent(`<p>Fout bij laden van inhoud</p>`);
      }
    };
    parseMarkdown();
  }, [section.content]);

  // Extract path if available
  const path = hasBreadcrumbPath(section) ? section.path : [];
  // Show breadcrumb when onNavigate is provided (allows navigation back to overview)
  const showBreadcrumb = !!onNavigate;

  return (
    <article className="bg-white rounded-lg border border-gray-200 p-8">
      {/* Breadcrumb navigation - Story 1-4 */}
      {showBreadcrumb && onNavigate && (
        <ManualBreadcrumb
          path={path}
          currentTitle={section.title}
          onNavigate={onNavigate}
        />
      )}

      <header className="mb-6 pb-6 border-b border-gray-200">
        <h1 className="text-3xl font-bold text-gray-900">{section.title}</h1>
        {section.updatedAt && (
          <p className="text-sm text-gray-500 mt-2">
            Laatst bijgewerkt: {new Date(section.updatedAt).toLocaleDateString('nl-NL', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        )}
      </header>

      <div
        data-testid="manual-content"
        className="prose prose-blue max-w-none
          prose-headings:font-semibold
          prose-h1:text-2xl prose-h1:mb-4
          prose-h2:text-xl prose-h2:mb-3 prose-h2:mt-8
          prose-h3:text-lg prose-h3:mb-2 prose-h3:mt-6
          prose-p:mb-4 prose-p:leading-relaxed
          prose-ul:mb-4 prose-ul:list-disc prose-ul:pl-6
          prose-ol:mb-4 prose-ol:list-decimal prose-ol:pl-6
          prose-li:mb-2
          prose-a:text-blue-600 prose-a:underline hover:prose-a:text-blue-800
          prose-strong:font-semibold prose-strong:text-gray-900
          prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
          prose-pre:bg-gray-100 prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto
          prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:pl-4 prose-blockquote:italic
        "
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    </article>
  );
}
